import { PrismicRichText } from '@prismicio/react';
import VisuallyHidden from '@reach/visually-hidden';
import Image from 'next/image';
import Plyr from 'plyr';
import { useEffect, useRef } from 'react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import {
  SpeakersSection as SpeakersSectionBase,
  SectionHeader,
  SectionHeaderTitle,
  SectionHeaderDescription,
  SectionGrid,
  SpeakerContainer,
  SpeakerImage as SpeakerImageBase,
  SpeakerName,
  SpeakerTitle,
  SpeakerDescription,
  SocialLinks,
  SocialLinksWrapper,
  IllustrationWrapper,
  ImageWrapper,
  ImageContainer,
} from '../SpeakersSlice';
import BoardIcon from '../assets/board.svg';
import LinkedinIcon from '../assets/linkedin.svg';
import BgIllustration from '../assets/speakers-image-bg.svg';
import TwitterIcon from '../assets/twitter.svg';

import { transformProperties } from '@/src/domains/prismic/helpers';
import { mobileBreakpoint, tabletBreakpoint } from '@/src/domains/shared/breakpoints';
import 'plyr/dist/plyr.css';

const SpeakersSection = styled(SpeakersSectionBase)`
  ${SpeakerContainer} {
    max-width: unset;
  }

  ${ImageWrapper} {
    border: 5px var(--colors-blank) solid;
    max-width: 214px;
    max-height: 214px;
  }
`;

const Container = styled(ContentContainer)`
  z-index: 1;
`;

const SpeakerImage = styled(SpeakerImageBase)`
  ${({ $imageBottom }) => {
    switch ($imageBottom) {
      case true:
        return css`
          top: -12%;
        `;
      case false:
      default:
        return css`
          /* add border width */
          top: calc(-107px - 10px);
        `;
    }
  }}
`;

const VideoWrapper = styled.div`
  /* margin-top: -200px; */
  margin-bottom: 80px;
  box-shadow: 0px 10px 24px rgba(36, 18, 124, 0.5);

  ${tabletBreakpoint(css`
    margin-top: 0;
  `)};

  iframe {
    ${tabletBreakpoint(css`
      ${fluidProp('width', '320px', '960px')}
    `)};

    ${mobileBreakpoint(css`
      width: auto;
      height: auto;
    `)};
  }
`;

const Video = styled.div`
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

const SpeakerVideo = ({ videoSrc }) => {
  const plyrRef = useRef(null);

  useEffect(() => {
    if (!plyrRef.current) return;

    plyrRef.current.plyr = new Plyr('#player-js', { captions: { active: true } });

    plyrRef.current.dataset.plyrEmbedId = videoSrc;
  }, [plyrRef, videoSrc]);

  return (
    <VideoWrapper>
      <Video data-plyr-provider="vimeo" ref={plyrRef} id="player-js" />
    </VideoWrapper>
  );
};

export function SpeakersSlice({ slice }) {
  const { primary, fields: speakers } = transformProperties(slice);
  const { vimeoVideoId, speakersTitle, speakersDescription } = primary;

  return (
    <SpeakersSection>
      <Container>
        <SectionHeader>
          <BoardIcon />
          <SectionHeaderTitle>
            <PrismicRichText field={speakersTitle} />
          </SectionHeaderTitle>
          <SectionHeaderDescription>
            <PrismicRichText field={speakersDescription} />
          </SectionHeaderDescription>
        </SectionHeader>
        <SectionGrid>
          {speakers.map((speaker, index) => {
            const {
              speakerName,
              speakerDescription,
              speakerTitle,
              illustration,
              linkedinLink,
              twitterLink,
              imageBottom,
            } = speaker;
            return (
              <SpeakerContainer key={index}>
                <SpeakerImage $imageBottom={imageBottom}>
                  <ImageContainer>
                    <ImageWrapper>
                      <Image
                        src={illustration.url}
                        alt={illustration.alt || ''}
                        width={illustration.dimensions.width}
                        height={illustration.dimensions.height}
                        objectFit="cover"
                        objectPosition="center top"
                        quality="100"
                      />
                    </ImageWrapper>
                  </ImageContainer>
                </SpeakerImage>
                {speakerName && (
                  <SpeakerName>
                    <PrismicRichText field={speakerName} />
                  </SpeakerName>
                )}
                {speakerTitle && (
                  <SpeakerTitle>
                    <PrismicRichText field={speakerTitle} />
                  </SpeakerTitle>
                )}
                <SocialLinksWrapper>
                  <SocialLinks>
                    {linkedinLink && (
                      <a href={linkedinLink?.url} target={linkedinLink?.target}>
                        <LinkedinIcon />
                        <VisuallyHidden>Linkedin</VisuallyHidden>
                      </a>
                    )}
                    {twitterLink && (
                      <a href={twitterLink?.url} target={twitterLink?.target}>
                        <TwitterIcon />
                        <VisuallyHidden>Twitter</VisuallyHidden>
                      </a>
                    )}
                  </SocialLinks>
                </SocialLinksWrapper>
                {speakerDescription && (
                  <SpeakerDescription>
                    <PrismicRichText field={speakerDescription} />
                  </SpeakerDescription>
                )}
              </SpeakerContainer>
            );
          })}
        </SectionGrid>
        {vimeoVideoId ? (
          <SpeakerVideo videoSrc={`https://player.vimeo.com/video/${vimeoVideoId}`} />
        ) : null}
      </Container>

      <IllustrationWrapper>
        <ImageContainer>
          <BgIllustration />
        </ImageContainer>
      </IllustrationWrapper>
    </SpeakersSection>
  );
}
